import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanificacion } from '@context/PlanificacionContext';
import { format, parseISO } from 'date-fns';
import { calendarioService } from '@services/calendarioService';
import { es } from 'date-fns/locale';
import { LayoutGrid, Columns, ArrowLeft, ArrowRight, Calendar as CalendarIcon, AlertCircle, Info } from 'lucide-react';
import Button from '@components/common/Button';

const PeriodosPage = () => {
  const navigate = useNavigate();
  const { 
    calendario, 
    tipoPeriodo, 
    periodos, 
    definirTipoPeriodo, 
    agregarPeriodo,
    actualizarPeriodo,
    semanasVacaciones,
    semanasGestion,
    marcarSemanasGestion, 
    marcarSemanasVacaciones,
    cargarCalendario 
  } = usePlanificacion();

  const [rangoUtil, setRangoUtil] = useState({ inicio: null, fin: null, semanas: [] });

  // Cargar el calendario si no existe en el contexto
  useEffect(() => {
    const fetchCalendario = async () => {
      if (!calendario || calendario.length === 0) {
        try {
          const response = await calendarioService.getByYear(2026);
          cargarCalendario(response.semanas || response || []); 
        } catch (err) {
          console.error('Error al cargar el calendario', err);
        }
      }
    };
    fetchCalendario();
  }, [calendario, cargarCalendario]);

  // Ordenar y preparar el rango útil
  useEffect(() => {
    if (calendario && calendario.length > 0) {
      const calOrdenado = [...calendario].sort((a, b) => new Date(a.fecha_inicio) - new Date(b.fecha_inicio));
      setRangoUtil({
        inicio: calOrdenado[0],
        fin: calOrdenado[calOrdenado.length - 1],
        semanas: calOrdenado
      });
    }
  }, [calendario]);

  const esSemanaValida = (semanaId) => {
    return !semanasVacaciones?.includes(semanaId) && !semanasGestion?.includes(semanaId);
  };

  const getInfoSemana = (semanaId) => {
    if (semanasVacaciones?.includes(semanaId)) return { tipo: 'vacaciones', icono: '🏖️', label: 'Vacaciones', bg: 'bg-yellow-50 border-yellow-200 text-yellow-800' };
    if (semanasGestion?.includes(semanaId)) return { tipo: 'gestion', icono: '⚙️', label: 'Gestión', bg: 'bg-purple-50 border-purple-200 text-purple-800' };
    return { tipo: 'vacia', icono: '⚪', label: 'Lectiva', bg: 'bg-gray-50 border-gray-200 text-gray-500' };
  };

  const formatearFecha = (fechaString) => {
    if (!fechaString) return '';
    // Extraemos solo la parte de la fecha (YYYY-MM-DD) para evitar problemas de zona horaria
    const soloFecha = fechaString.split('T')[0];
    return format(parseISO(soloFecha), "dd MMM", { locale: es });
  };

  // HELPER CORREGIDO: Encuentra a qué semana pertenece una fecha exacta (inicio o fin)
  const encontrarSemanaPorFecha = (fechaBuscadaStr) => {
    if (!rangoUtil.semanas.length) return null;
    
    // Convertimos la fecha buscada a un timestamp de mediodía (evita saltos por zona horaria)
    const objetivo = new Date(fechaBuscadaStr.substring(0, 10) + 'T12:00:00').getTime();
    
    // 1. Buscamos la semana donde la fecha objetivo caiga DENTRO de esa semana
    const semanaExacta = rangoUtil.semanas.find(sem => {
      const inicio = new Date(sem.fecha_inicio.substring(0, 10) + 'T00:00:00').getTime();
      
      // Tomamos fecha_fin, si no existe por error de BD, asumimos que la semana dura 7 días
      const finStr = sem.fecha_fin ? sem.fecha_fin.substring(0, 10) : null;
      const fin = finStr 
        ? new Date(finStr + 'T23:59:59').getTime() 
        : inicio + (6 * 24 * 60 * 60 * 1000);
        
      return objetivo >= inicio && objetivo <= fin;
    });

    if (semanaExacta) return semanaExacta.id;

    // 2. Fallback por si la fecha está totalmente fuera de tu calendario de BD
    let semanaMasCercana = rangoUtil.semanas[0];
    let menorDiferencia = Infinity;
    rangoUtil.semanas.forEach(sem => {
      const fechaSemana = new Date(sem.fecha_inicio.substring(0, 10) + 'T12:00:00').getTime();
      const dif = Math.abs(fechaSemana - objetivo);
      if (dif < menorDiferencia) {
        menorDiferencia = dif;
        semanaMasCercana = sem;
      }
    });
    return semanaMasCercana?.id;
  };

  const handleSeleccionarModalidad = (tipo) => {
    definirTipoPeriodo(tipo);

    // 1. Configurar Semanas Especiales (Fijas según RM 501-2025-MINEDU para 2026)
    // Gestión Inicial (2 semanas), Pausa Mayo (1 sem), Pausa Octubre (1 sem), Gestión Final (2 semanas)
    const fechasGestion = ['2026-03-02', '2026-03-09', '2026-05-18', '2026-10-12', '2026-12-21', '2026-12-28'];
    // Vacaciones Medio Año (2 semanas)
    const fechasVacaciones = ['2026-07-27', '2026-08-03'];
    
    const idsGestion = fechasGestion.map(f => encontrarSemanaPorFecha(f)).filter(Boolean);
    const idsVacaciones = fechasVacaciones.map(f => encontrarSemanaPorFecha(f)).filter(Boolean);

    if (marcarSemanasGestion) marcarSemanasGestion(idsGestion);
    if (marcarSemanasVacaciones) marcarSemanasVacaciones(idsVacaciones);

    // 2. Configurar Periodos Lectivos Exactos
    if (tipo === 'bimestre') {
      const bimestres = [
        { id: 1, num: 1, ini: '2026-03-16', fin: '2026-05-15' },
        { id: 2, num: 2, ini: '2026-05-25', fin: '2026-07-24' },
        { id: 3, num: 3, ini: '2026-08-10', fin: '2026-10-09' },
        { id: 4, num: 4, ini: '2026-10-19', fin: '2026-12-18' }
      ];
      bimestres.forEach(b => {
        agregarPeriodo({
          id: b.id, numero: b.num, tipo: 'bimestre', nombre: `Bimestre ${b.num}`,
          semana_inicio_id: encontrarSemanaPorFecha(b.ini),
          semana_fin_id: encontrarSemanaPorFecha(b.fin), // El backend busca el lunes más cercano a esta fecha de fin
        });
      });
    } else {
      const trimestres = [
        { id: 1, num: 1, ini: '2026-03-16', fin: '2026-06-12' }, 
        { id: 2, num: 2, ini: '2026-06-15', fin: '2026-09-18' }, 
        { id: 3, num: 3, ini: '2026-09-21', fin: '2026-12-18' }  
      ];
      trimestres.forEach(t => {
        agregarPeriodo({
          id: t.id, numero: t.num, tipo: 'trimestre', nombre: `Trimestre ${t.num}`,
          semana_inicio_id: encontrarSemanaPorFecha(t.ini),
          semana_fin_id: encontrarSemanaPorFecha(t.fin), 
        });
      });
    }
  };

  const handleCambiarLimite = (periodoIndex, campo, nuevoSemanaIdStr) => {
    const nuevoSemanaId = parseInt(nuevoSemanaIdStr);
    const periodoActual = periodos[periodoIndex];
    const nuevoIdx = rangoUtil.semanas.findIndex(s => s.id === nuevoSemanaId);
    
    actualizarPeriodo(periodoActual.id, { [campo]: nuevoSemanaId });

    if (campo === 'semana_fin_id' && periodoIndex < periodos.length - 1) {
      let nextValidSemana = null;
      for (let i = nuevoIdx + 1; i < rangoUtil.semanas.length; i++) {
        if (esSemanaValida(rangoUtil.semanas[i].id)) {
          nextValidSemana = rangoUtil.semanas[i];
          break;
        }
      }
      if (nextValidSemana) actualizarPeriodo(periodos[periodoIndex + 1].id, { semana_inicio_id: nextValidSemana.id });
    }
    
    if (campo === 'semana_inicio_id' && periodoIndex > 0) {
      let prevValidSemana = null;
      for (let i = nuevoIdx - 1; i >= 0; i--) {
        if (esSemanaValida(rangoUtil.semanas[i].id)) {
          prevValidSemana = rangoUtil.semanas[i];
          break;
        }
      }
      if (prevValidSemana) actualizarPeriodo(periodos[periodoIndex - 1].id, { semana_fin_id: prevValidSemana.id });
    }
  };

  const agruparSemanasParaMapa = () => {
    const grupos = [];
    let grupoActual = { tipo: 'fuera', semanas: [] };

    rangoUtil.semanas.forEach((sem, indexGlobal) => {
      const periodoAsignado = periodos.find(p => {
        const iStart = rangoUtil.semanas.findIndex(s => s.id === p.semana_inicio_id);
        const iEnd = rangoUtil.semanas.findIndex(s => s.id === p.semana_fin_id);
        return indexGlobal >= iStart && indexGlobal <= iEnd;
      });

      if (periodoAsignado) {
        if (grupoActual.tipo === 'fuera' || grupoActual.periodo?.id !== periodoAsignado.id) {
          if (grupoActual.semanas.length > 0) grupos.push(grupoActual);
          grupoActual = { tipo: 'periodo', periodo: periodoAsignado, semanas: [sem] };
        } else {
          grupoActual.semanas.push(sem);
        }
      } else {
        if (grupoActual.tipo === 'periodo') {
          if (grupoActual.semanas.length > 0) grupos.push(grupoActual);
          grupoActual = { tipo: 'fuera', semanas: [sem] };
        } else {
          grupoActual.semanas.push(sem);
        }
      }
    });
    
    if (grupoActual.semanas.length > 0) grupos.push(grupoActual);
    return grupos;
  };

  if (!calendario || calendario.length === 0) return (
    <div className="max-w-4xl mx-auto p-6 text-center mt-20"><AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" /><h2 className="text-2xl font-bold text-gray-800 mb-2">Cargando calendario base...</h2></div>
  );

  const coloresBimestres = [
    { text: 'text-blue-700', border: 'border-blue-300', bgClaro: 'bg-blue-50', header: 'bg-blue-100', linea: 'border-blue-500' },
    { text: 'text-green-700', border: 'border-green-300', bgClaro: 'bg-green-50', header: 'bg-green-100', linea: 'border-green-500' },
    { text: 'text-orange-700', border: 'border-orange-300', bgClaro: 'bg-orange-50', header: 'bg-orange-100', linea: 'border-orange-500' },
    { text: 'text-purple-700', border: 'border-purple-300', bgClaro: 'bg-purple-50', header: 'bg-purple-100', linea: 'border-purple-500' }
  ];

  const gruposMapa = agruparSemanasParaMapa();

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex items-center justify-between mb-8 pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-blue-600" /> Organización de Periodos
          </h1>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/planificacion')} className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Volver</Button>
          {periodos.length > 0 && <Button onClick={() => navigate('/planificacion')} className="bg-blue-600 text-white flex items-center gap-2">Ir a Planificar Sesiones <ArrowRight className="w-4 h-4" /></Button>}
        </div>
      </div>

      {periodos.length === 0 ? (
        <div className="max-w-3xl mx-auto mt-12 grid md:grid-cols-2 gap-6">
          <button onClick={() => handleSeleccionarModalidad('bimestre')} className="flex flex-col items-center p-8 bg-white border-2 border-transparent rounded-2xl shadow-sm hover:border-blue-500 transition-all">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4"><LayoutGrid className="w-10 h-10 text-blue-600" /></div>
            <h3 className="text-2xl font-bold text-gray-800">Bimestral (4)</h3>
          </button>
          <button onClick={() => handleSeleccionarModalidad('trimestre')} className="flex flex-col items-center p-8 bg-white border-2 border-transparent rounded-2xl shadow-sm hover:border-green-500 transition-all">
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-4"><Columns className="w-10 h-10 text-green-600" /></div>
            <h3 className="text-2xl font-bold text-gray-800">Trimestral (3)</h3>
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex justify-between items-center">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <p className="text-blue-800 text-sm">
                Ajusta el inicio y fin. <strong>El sistema saltará automáticamente las semanas de vacaciones y gestión</strong> para no interrumpir tus periodos.
              </p>
            </div>
            <Button variant="outline" onClick={() => definirTipoPeriodo(null)} className="text-xs bg-white">Cambiar Modalidad</Button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {periodos.map((periodo, index) => {
              const idxInicio = rangoUtil.semanas.findIndex(s => s.id === periodo.semana_inicio_id);
              const idxFin = rangoUtil.semanas.findIndex(s => s.id === periodo.semana_fin_id);
              
              const semanasCalendario = (idxFin >= 0 && idxInicio >= 0) ? (idxFin - idxInicio + 1) : 0;
              let semanasEfectivas = 0;
              if (semanasCalendario > 0) {
                for (let i = idxInicio; i <= idxFin; i++) {
                  if (esSemanaValida(rangoUtil.semanas[i].id)) semanasEfectivas++;
                }
              }

              const colorBase = coloresBimestres[index % 4];

              return (
                <div key={periodo.id} className="bg-white rounded-xl shadow-sm border p-5 relative overflow-hidden">
                  <div className={`absolute top-0 left-0 w-full h-1 bg-${colorBase.linea.split('-')[1]}-500`} />
                  
                  <div className="mb-4 pb-2 border-b flex justify-between items-end">
                    <h3 className="text-lg font-bold text-gray-800">{periodo.nombre}</h3>
                    <div className="text-right">
                      <span className={`text-sm font-semibold px-2 py-0.5 rounded ${colorBase.bgClaro} ${colorBase.text}`}>
                        {semanasEfectivas} sem. efectivas
                      </span>
                      {semanasCalendario !== semanasEfectivas && (
                        <p className="text-[10px] text-gray-500 mt-1">({semanasCalendario} en calendario)</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Empieza en:</label>
                      <select 
                        value={periodo.semana_inicio_id}
                        onChange={(e) => handleCambiarLimite(index, 'semana_inicio_id', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-50 focus:ring-blue-500"
                      >
                        {rangoUtil.semanas.map((sem, i) => {
                          const info = getInfoSemana(sem.id);
                          const esInvalida = !esSemanaValida(sem.id);
                          const cruceLogico = i > idxFin || (index > 0 && i <= rangoUtil.semanas.findIndex(s => s.id === periodos[index-1].semana_inicio_id));
                          
                          return (
                            <option key={`ini-${sem.id}`} value={sem.id} disabled={cruceLogico || esInvalida}>
                              Semana {sem.numero_semana} ({formatearFecha(sem.fecha_inicio)}) - {info.icono} {info.label} {esInvalida ? '(Bloqueada)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Termina en:</label>
                      <select 
                        value={periodo.semana_fin_id}
                        onChange={(e) => handleCambiarLimite(index, 'semana_fin_id', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm bg-gray-50 focus:ring-blue-500"
                      >
                        {rangoUtil.semanas.map((sem, i) => {
                          const info = getInfoSemana(sem.id);
                          const esInvalida = !esSemanaValida(sem.id);
                          const cruceLogico = i < idxInicio || (index < periodos.length - 1 && i >= rangoUtil.semanas.findIndex(s => s.id === periodos[index+1].semana_fin_id));
                          
                          return (
                            <option key={`fin-${sem.id}`} value={sem.id} disabled={cruceLogico || esInvalida}>
                              Semana {sem.numero_semana} ({formatearFecha(sem.fecha_fin)}) - {info.icono} {info.label} {esInvalida ? '(Bloqueada)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 mt-8">
            <h3 className="text-md font-bold text-gray-800 mb-6 border-b pb-2">Línea de Tiempo por Periodos</h3>
            
            <div className="flex flex-col gap-4">
              {gruposMapa.map((grupo, gIdx) => {
                if (grupo.tipo === 'fuera') {
                  // Determinamos si es Vacaciones o Gestión analizando la primera semana del grupo
                  const esVacaciones = semanasVacaciones?.includes(grupo.semanas[0]?.id);
                  const tituloBloque = esVacaciones ? 'Vacaciones' : 'Semanas de Gestión';

                  return (
                    <div key={`grupo-${gIdx}`} className="p-4 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                      <p className="text-xs font-bold text-gray-500 uppercase mb-3">{tituloBloque}</p>
                      <div className="flex flex-wrap gap-2">
                        {grupo.semanas.map(sem => {
                          const info = getInfoSemana(sem.id);
                          return (
                            <div key={sem.id} className={`flex flex-col items-center justify-center w-14 h-16 rounded border opacity-70 ${info.bg}`} title={`${info.icono} Semana ${sem.numero_semana}: ${info.label}\n${formatearFecha(sem.fecha_inicio)}`}>
                              <span className="text-xs font-bold text-gray-700">{sem.numero_semana}</span>
                              <span className="text-[10px] text-gray-600">{formatearFecha(sem.fecha_inicio)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const pIdx = periodos.findIndex(p => p.id === grupo.periodo.id);
                const color = coloresBimestres[pIdx % 4];

                return (
                  <div key={`grupo-${gIdx}`} className={`border-2 ${color.border} rounded-xl overflow-hidden shadow-sm`}>
                    <div className={`px-4 py-2 ${color.header} border-b ${color.border} flex justify-between items-center`}>
                      <h4 className={`font-bold ${color.text}`}>{grupo.periodo.nombre}</h4>
                      <span className={`text-xs font-bold ${color.text} bg-white px-2 py-1 rounded-md shadow-sm`}>
                        {grupo.semanas.length} semanas
                      </span>
                    </div>
                    <div className="p-4 bg-white">
                      <div className="flex flex-wrap gap-2">
                        {grupo.semanas.map(sem => {
                          const info = getInfoSemana(sem.id);
                          return (
                            <div 
                              key={sem.id} 
                              className={`flex flex-col items-center justify-center w-14 h-16 rounded border ${info.bg} relative group cursor-help transition-transform hover:-translate-y-1 shadow-sm`}
                              title={`${grupo.periodo.nombre}\n${info.icono} Semana ${sem.numero_semana}: ${info.label}\n${formatearFecha(sem.fecha_inicio)}`}
                            >
                              <span className="text-xs font-bold text-gray-700">{sem.numero_semana}</span>
                              <span className="text-[10px] opacity-70 text-gray-600">{formatearFecha(sem.fecha_inicio)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="flex flex-wrap gap-6 mt-6 pt-4 border-t">
              <span className="text-xs font-bold text-gray-500 uppercase mr-2">Leyenda Visual:</span>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-600"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></span> Sesión</div>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-600"><span className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></span> Vacaciones</div>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-600"><span className="w-3 h-3 rounded bg-purple-100 border border-purple-300"></span> Gestión</div>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-600"><span className="w-3 h-3 rounded border-2 border-dashed border-gray-300 bg-gray-50"></span> Semanas de Gestión / Vacaciones</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodosPage;